import { ReactNode, useState } from "react";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { Profile, Roles } from "@/types/auth/auth";
import { GenieCategories } from "@/types/shared/categories";
import { useLlmModels } from "@/hooks/voice/useLlmModels";
import { profileLabel, UserSearchPicker } from "@/components/filters/UserFilter";
import { FilterOption, FilterOptionIcon, FilterPill, OptionPill } from "@/components/filters/FilterPill";
import { ProviderIcon } from "@/components/agent/core/ProviderIcon";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { LLMModel } from "@/utils/config/elevenlabs";

type Flag = boolean | "";
type FlagValue = "true" | "false";

const toFlagValue = (flag: Flag | undefined): FlagValue | null =>
  flag === "" || flag === undefined ? null : flag ? "true" : "false";

const fromFlagValue = (value: FlagValue | null): Flag => (value === null ? "" : value === "true");

const ROLE_OPTIONS: FilterOption<string>[] = [
  { value: null, label: "All roles" },
  { value: Roles.Admin, label: "Admin" },
  { value: Roles.Business, label: "Business" },
  { value: Roles.Consumer, label: "Consumer" },
];

const CATEGORY_OPTIONS: FilterOption<string>[] = [
  { value: null, label: "All categories" },
  ...GenieCategories.map((cat) => ({ value: cat.id, label: cat.label })),
];

const STATUS_OPTIONS: FilterOption<FlagValue>[] = [
  { value: null, label: "Any status" },
  { value: "true", label: "Active" },
  { value: "false", label: "Inactive" },
];

const VISIBILITY_OPTIONS: FilterOption<FlagValue>[] = [
  { value: null, label: "Public and private" },
  { value: "true", label: "Public" },
  { value: "false", label: "Private" },
];

const YES_NO_OPTIONS: FilterOption<FlagValue>[] = [
  { value: null, label: "All" },
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

const REPORT_MODE_OPTIONS: FilterOption<string>[] = [
  { value: null, label: "All" },
  { value: "auto", label: "Auto (AI decides)" },
  { value: "every_conversation", label: "Every conversation" },
  { value: "digest", label: "Scheduled digest" },
  { value: "escalation_only", label: "Only when attention needed" },
  { value: "off", label: "Off" },
];

const optionLabel = <T extends string>(options: FilterOption<T>[], value: T | null) =>
  options.find((o) => o.value === value)?.label ?? value ?? "";

const providerIcons = new Map<LLMModel["provider"], FilterOptionIcon>();
const providerIcon = (provider: LLMModel["provider"]): FilterOptionIcon => {
  let Icon = providerIcons.get(provider);
  if (!Icon) {
    Icon = ({ className }) => <ProviderIcon provider={provider} className={className} />;
    providerIcons.set(provider, Icon);
  }
  return Icon;
};

interface AgentFiltersProps {
  searchTerm: string;
  selectedRole: string;
  selectedCategory: string;
  selectedLlmModel?: string;
  selectedVoiceLabel?: string;
  selectedIsActive?: Flag;
  selectedIsPublic?: Flag;
  selectedIsDemoGenie?: Flag;
  selectedIsMarketplaceGenie?: Flag;
  selectedReportMode?: string;
  selectedUserId?: string | null;
  dailyCallLimit?: number | null;
  onSearchChange: (value: string) => void;
  onRoleChange: (value: string) => void;
  onCategoryChange: (value: string) => void;
  onLlmModelChange?: (value: string) => void;
  onVoiceLabelChange?: (value: string) => void;
  onIsActiveChange?: (value: Flag) => void;
  onIsPublicChange?: (value: Flag) => void;
  onIsDemoGenieChange?: (value: Flag) => void;
  onIsMarketplaceGenieChange?: (value: Flag) => void;
  onReportModeChange?: (value: string) => void;
  onUserChange?: (value: string | null) => void;
  onDailyCallLimitChange?: (value: number | null) => void;
  onClearFilters: () => void;
  showAdvancedFilters?: boolean;
  /** Rendered at the end of the bar, e.g. a view toggle. */
  actions?: ReactNode;
}

/** One toolbar for narrowing the genie list, in the same pill style as the inbox filter bar. */
export const AgentFilters = ({
  searchTerm,
  selectedRole,
  selectedCategory,
  selectedLlmModel,
  selectedVoiceLabel,
  selectedIsActive,
  selectedIsPublic,
  selectedIsDemoGenie,
  selectedIsMarketplaceGenie,
  selectedReportMode,
  selectedUserId,
  dailyCallLimit,
  onSearchChange,
  onRoleChange,
  onCategoryChange,
  onLlmModelChange,
  onVoiceLabelChange,
  onIsActiveChange,
  onIsPublicChange,
  onIsDemoGenieChange,
  onIsMarketplaceGenieChange,
  onReportModeChange,
  onUserChange,
  onDailyCallLimitChange,
  onClearFilters,
  showAdvancedFilters = false,
  actions,
}: AgentFiltersProps) => {
  const [selectedProfile, setSelectedProfile] = useState<Profile | null>(null);
  const { data: llmModels = [] } = useLlmModels({ enabled: showAdvancedFilters });

  const modelOptions: FilterOption<string>[] = [
    { value: null, label: "All models" },
    ...llmModels.map((model) => ({ value: model.id, label: model.name, Icon: providerIcon(model.provider) })),
  ];
  const selectedModel = llmModels.find((m) => m.id === selectedLlmModel);

  const userLabel = !selectedUserId
    ? "All"
    : selectedProfile?.id === selectedUserId
      ? profileLabel(selectedProfile)
      : "Selected user";

  const moreCount = [
    selectedIsDemoGenie !== "" && selectedIsDemoGenie !== undefined,
    selectedIsMarketplaceGenie !== "" && selectedIsMarketplaceGenie !== undefined,
    Boolean(selectedReportMode),
    Boolean(selectedVoiceLabel),
    Boolean(dailyCallLimit),
  ].filter(Boolean).length;

  const hasActiveFilters = Boolean(
    searchTerm ||
      selectedRole ||
      selectedCategory ||
      selectedLlmModel ||
      toFlagValue(selectedIsActive) ||
      toFlagValue(selectedIsPublic) ||
      selectedUserId ||
      moreCount,
  );

  return (
    <div className="flex flex-col gap-3 lg:flex-row lg:items-start">
      <div className="flex flex-1 flex-wrap items-center gap-2">
        <div className="w-full sm:w-64 shrink-0 p-px rounded-xl bg-[#E5EAF0] focus-within:bg-genie-primary/40 transition-colors">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search genies..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full min-h-[42px] sm:min-h-0 rounded-[11px] bg-white py-[9px] pl-9 pr-9 text-base sm:text-sm leading-5 text-gray-900 placeholder:text-gray-400 outline-none"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => onSearchChange("")}
                aria-label="Clear search"
                className="absolute right-1 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {onUserChange && (
          <UserSearchPicker
            value={selectedUserId ?? null}
            onChange={(profile) => {
              setSelectedProfile(profile);
              onUserChange(profile?.id ?? null);
            }}
          >
            <FilterPill active={Boolean(selectedUserId)} label="User">
              <span className="truncate">{userLabel}</span>
            </FilterPill>
          </UserSearchPicker>
        )}

        <OptionPill
          label="Category"
          valueLabel={selectedCategory ? optionLabel(CATEGORY_OPTIONS, selectedCategory) : "All"}
          active={Boolean(selectedCategory)}
          options={CATEGORY_OPTIONS}
          selected={selectedCategory || null}
          onSelect={(value) => onCategoryChange(value ?? "")}
        />

        <OptionPill
          label="Role"
          valueLabel={selectedRole ? optionLabel(ROLE_OPTIONS, selectedRole) : "All"}
          active={Boolean(selectedRole)}
          options={ROLE_OPTIONS}
          selected={selectedRole || null}
          onSelect={(value) => onRoleChange(value ?? "")}
        />

        {onIsActiveChange && (
          <OptionPill
            label="Status"
            valueLabel={toFlagValue(selectedIsActive) ? optionLabel(STATUS_OPTIONS, toFlagValue(selectedIsActive)) : "All"}
            active={toFlagValue(selectedIsActive) !== null}
            options={STATUS_OPTIONS}
            selected={toFlagValue(selectedIsActive)}
            onSelect={(value) => onIsActiveChange(fromFlagValue(value))}
          />
        )}

        {onIsPublicChange && (
          <OptionPill
            label="Visibility"
            valueLabel={toFlagValue(selectedIsPublic) ? optionLabel(VISIBILITY_OPTIONS, toFlagValue(selectedIsPublic)) : "All"}
            active={toFlagValue(selectedIsPublic) !== null}
            options={VISIBILITY_OPTIONS}
            selected={toFlagValue(selectedIsPublic)}
            onSelect={(value) => onIsPublicChange(fromFlagValue(value))}
          />
        )}

        {showAdvancedFilters && onLlmModelChange && (
          <OptionPill
            label="Model"
            valueLabel={selectedModel?.name ?? (selectedLlmModel || "All")}
            ValueIcon={selectedModel ? providerIcon(selectedModel.provider) : undefined}
            active={Boolean(selectedLlmModel)}
            options={modelOptions}
            selected={selectedLlmModel || null}
            onSelect={(value) => onLlmModelChange(value ?? "")}
          />
        )}

        {showAdvancedFilters && (
          <MoreFiltersPill
            count={moreCount}
            isDemoGenie={selectedIsDemoGenie}
            isMarketplaceGenie={selectedIsMarketplaceGenie}
            reportMode={selectedReportMode}
            voiceLabel={selectedVoiceLabel}
            dailyCallLimit={dailyCallLimit}
            onIsDemoGenieChange={onIsDemoGenieChange}
            onIsMarketplaceGenieChange={onIsMarketplaceGenieChange}
            onReportModeChange={onReportModeChange}
            onVoiceLabelChange={onVoiceLabelChange}
            onDailyCallLimitChange={onDailyCallLimitChange}
          />
        )}

        {hasActiveFilters && (
          <button
            type="button"
            onClick={() => {
              setSelectedProfile(null);
              onClearFilters();
            }}
            className="min-h-[44px] sm:min-h-0 shrink-0 inline-flex items-center gap-1 px-1.5 text-xs font-semibold text-gray-500 hover:text-genie-primary transition-colors"
          >
            <X className="h-3.5 w-3.5" />
            Clear
          </button>
        )}
      </div>

      {actions && <div className="shrink-0">{actions}</div>}
    </div>
  );
};

interface MoreFiltersPillProps {
  count: number;
  isDemoGenie?: Flag;
  isMarketplaceGenie?: Flag;
  reportMode?: string;
  voiceLabel?: string;
  dailyCallLimit?: number | null;
  onIsDemoGenieChange?: (value: Flag) => void;
  onIsMarketplaceGenieChange?: (value: Flag) => void;
  onReportModeChange?: (value: string) => void;
  onVoiceLabelChange?: (value: string) => void;
  onDailyCallLimitChange?: (value: number | null) => void;
}

const MoreFiltersPill = ({
  count,
  isDemoGenie,
  isMarketplaceGenie,
  reportMode,
  voiceLabel,
  dailyCallLimit,
  onIsDemoGenieChange,
  onIsMarketplaceGenieChange,
  onReportModeChange,
  onVoiceLabelChange,
  onDailyCallLimitChange,
}: MoreFiltersPillProps) => (
  <Popover>
    <PopoverTrigger asChild>
      <FilterPill active={count > 0}>
        <SlidersHorizontal className="h-4 w-4 shrink-0 text-genie-primary" />
        <span>More filters</span>
        {count > 0 && (
          <span className="rounded-full bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] px-1.5 text-[11px] font-bold leading-[18px] text-white">
            {count}
          </span>
        )}
      </FilterPill>
    </PopoverTrigger>
    <PopoverContent align="start" sideOffset={6} className="w-[min(22rem,calc(100vw-2rem))] space-y-4 p-4">
      {onIsDemoGenieChange && (
        <FilterSection label="Demo genie">
          <ChoiceChips
            options={YES_NO_OPTIONS}
            selected={toFlagValue(isDemoGenie)}
            onSelect={(value) => onIsDemoGenieChange(fromFlagValue(value))}
          />
        </FilterSection>
      )}
      {onIsMarketplaceGenieChange && (
        <FilterSection label="Marketplace genie">
          <ChoiceChips
            options={YES_NO_OPTIONS}
            selected={toFlagValue(isMarketplaceGenie)}
            onSelect={(value) => onIsMarketplaceGenieChange(fromFlagValue(value))}
          />
        </FilterSection>
      )}
      {onReportModeChange && (
        <FilterSection label="Report mode">
          <ChoiceChips
            options={REPORT_MODE_OPTIONS}
            selected={reportMode || null}
            onSelect={(value) => onReportModeChange(value ?? "")}
          />
        </FilterSection>
      )}
      {onVoiceLabelChange && (
        <FilterSection label="Voice label">
          <PopoverInput
            placeholder="e.g. British, warm"
            value={voiceLabel || ""}
            onChange={(value) => onVoiceLabelChange(value)}
          />
        </FilterSection>
      )}
      {onDailyCallLimitChange && (
        <FilterSection label="Daily call limit">
          <PopoverInput
            type="number"
            placeholder="Any limit"
            value={dailyCallLimit ? String(dailyCallLimit) : ""}
            onChange={(value) => onDailyCallLimitChange(value ? parseInt(value, 10) : null)}
          />
        </FilterSection>
      )}
    </PopoverContent>
  </Popover>
);

const FilterSection = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="space-y-1.5">
    <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">{label}</div>
    {children}
  </div>
);

function ChoiceChips<T extends string>({
  options,
  selected,
  onSelect,
}: {
  options: FilterOption<T>[];
  selected: T | null;
  onSelect: (value: T | null) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((option) => {
        const isOn = option.value === selected;
        return (
          <button
            key={option.value ?? "all"}
            type="button"
            onClick={() => onSelect(option.value)}
            aria-pressed={isOn}
            className={cn(
              "min-h-[44px] sm:min-h-[32px] rounded-lg border px-2.5 text-xs transition-colors",
              isOn
                ? "border-genie-primary/40 bg-genie-primary/10 font-semibold text-genie-primary"
                : "border-[#E5EAF0] bg-white text-gray-600 hover:border-genie-primary/40",
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

const PopoverInput = ({
  value,
  onChange,
  placeholder,
  type = "text",
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  type?: "text" | "number";
}) => (
  <input
    type={type}
    min={type === "number" ? 0 : undefined}
    value={value}
    placeholder={placeholder}
    onChange={(e) => onChange(e.target.value)}
    className="w-full min-h-[44px] sm:min-h-[36px] rounded-lg border border-[#E5EAF0] bg-white px-3 text-base sm:text-sm text-gray-900 placeholder:text-gray-400 outline-none focus:border-genie-primary/40 focus:ring-2 focus:ring-genie-primary/20"
  />
);
